"use client";
import { Alert, Button } from "@softmax/design-system";
import { spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useId, type ReactNode } from "react";
import { AccountProfile } from "./account-profile";
import { CommunicationPreferences } from "./communication-preferences";
import { LinkedAccounts, type LinkedAccount } from "./linked-accounts";
import type { AccountProfileDraft, AccountSaveState } from "./profile-state";

const styles = stylex.create({
  form: { display: "grid", gap: spacing.space24 },
  fieldset: {
    borderWidth: 0,
    margin: 0,
    minWidth: 0,
    padding: 0,
    display: "grid",
    gap: spacing.space24,
  },
  actions: { display: "flex", justifyContent: "flex-end" },
});

export function AccountSettings({
  profile,
  avatar,
  linkedAccounts,
  save,
  refreshError,
  disabled = false,
  onProfileChange,
  onSave,
  onRetry,
}: {
  readonly profile: AccountProfileDraft;
  readonly avatar: ReactNode;
  readonly linkedAccounts: readonly LinkedAccount[];
  readonly save: AccountSaveState;
  readonly refreshError?: string;
  readonly disabled?: boolean;
  readonly onProfileChange: (changes: Partial<AccountProfileDraft>) => void;
  readonly onSave: () => void;
  readonly onRetry: () => void;
}) {
  const id = useId();
  const saving = save.status === "saving";
  return (
    <form
      {...stylex.props(styles.form)}
      onSubmit={(event) => {
        event.preventDefault();

        if (!saving && !disabled && save.status !== "uncertain") onSave();
      }}
    >
      {save.status === "error" || save.status === "uncertain" ? (
        <Alert tone="danger" announcement="assertive">
          {save.message}
          {save.status === "uncertain" ? (
            <Button type="button" size="sm" onClick={onRetry}>
              Refresh account
            </Button>
          ) : null}
        </Alert>
      ) : null}
      {save.status === "saved" ? <Alert>Account settings saved.</Alert> : null}
      {refreshError ? (
        <Alert tone="danger">
          {refreshError}{" "}
          <Button type="button" size="sm" onClick={onRetry}>
            Refresh account
          </Button>
        </Alert>
      ) : null}
      <fieldset disabled={saving} {...stylex.props(styles.fieldset)}>
        <AccountProfile
          id={id}
          profile={profile}
          avatar={avatar}
          onProfileChange={onProfileChange}
        />
        <CommunicationPreferences
          profile={profile}
          onProfileChange={onProfileChange}
        />
        {linkedAccounts.length > 0 ? (
          <LinkedAccounts accounts={linkedAccounts} />
        ) : null}
        <div {...stylex.props(styles.actions)}>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || disabled || save.status === "uncertain"}
          >
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
}
